import { PrintTechPixelStats, RasterPixels } from './types';

// Alpha thresholds (0..255).
const ALPHA_VISIBLE = 13; // ~0.05 * 255
const ALPHA_OPAQUE = 153; // ~0.6 * 255
const ALPHA_STRONG_OPAQUE = 230; // ~0.9 * 255

// Sobel luminance-gradient magnitude bands. The 3x3 kernel scales a per-pixel
// luminance delta by up to ~4x, so a hard two-tone edge lands near ~1020 while
// gentle-to-moderate tonal transitions sit well below. Smooth = gradient/photographic
// content; strong = crisp lines / small text / logo edges (fine detail).
const SMOOTH_GRADIENT_MIN = 2;
const SMOOTH_GRADIENT_MAX = 80;
const STRONG_EDGE_MIN = 150;

// Keep the top 4 bits of each 8-bit channel -> a 12-bit colour key. This tolerates
// JPEG noise / anti-aliasing so near-identical colours collapse into one bucket.
// 16 levels per channel = the top 4 bits, expressed with arithmetic (no bitwise).
const POSTERIZE_LEVELS = 16;
const POSTERIZE_STEP = 256 / POSTERIZE_LEVELS;

function posterizedColorKey(r: number, g: number, b: number): number {
  const rb = Math.floor(r / POSTERIZE_STEP);
  const gb = Math.floor(g / POSTERIZE_STEP);
  const bb = Math.floor(b / POSTERIZE_STEP);
  return rb * POSTERIZE_LEVELS * POSTERIZE_LEVELS + gb * POSTERIZE_LEVELS + bb;
}

function luminance(r: number, g: number, b: number): number {
  // Rec.601 luma, integer-weighted.
  return (r * 299 + g * 587 + b * 114) / 1000;
}

function emptyStats(): PrintTechPixelStats {
  return {
    sampledPixels: 0,
    visiblePixels: 0,
    opaquePixels: 0,
    strongOpaquePixels: 0,
    semiTransparentPixels: 0,
    smoothGradientPixels: 0,
    strongEdgePixels: 0,
    colorBucketCounts: {},
  };
}

/**
 * Pure artwork analyzer over raw RGBA pixels. Returns aggregatable pixel-level
 * counts (not ratios) describing colour complexity, gradients, fine detail and
 * transparency. DOM-free so it can be unit-tested with hand-built rasters.
 *
 * GAPI-43148: feeds the host's DTF-vs-DTG recommendation. The editor does not
 * apply any thresholds/decision here.
 */
function analyzeImageDataStats(raster: RasterPixels): PrintTechPixelStats {
  const { data, width, height } = raster;
  const stats = emptyStats();

  if (!width || !height || data.length < 4) {
    return stats;
  }

  const pixelCount = width * height;
  // Precompute luminance and an opaque mask so the Sobel pass can read neighbours cheaply.
  const lum = new Float32Array(pixelCount);
  const opaqueMask = new Uint8Array(pixelCount);

  for (let p = 0; p < pixelCount; p += 1) {
    const i = p * 4;
    const alpha = data[i + 3];
    lum[p] = luminance(data[i], data[i + 1], data[i + 2]);

    stats.sampledPixels += 1;

    if (alpha <= ALPHA_VISIBLE) {
      continue;
    }
    stats.visiblePixels += 1;

    if (alpha < ALPHA_STRONG_OPAQUE) {
      stats.semiTransparentPixels += 1;
    } else {
      stats.strongOpaquePixels += 1;
    }

    if (alpha >= ALPHA_OPAQUE) {
      stats.opaquePixels += 1;
      opaqueMask[p] = 1;

      const key = posterizedColorKey(data[i], data[i + 1], data[i + 2]);
      stats.colorBucketCounts[key] = (stats.colorBucketCounts[key] || 0) + 1;
    }
  }

  // Sobel pass over opaque interior pixels only (edges of the raster are skipped).
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = y * width + x;
      if (!opaqueMask[p]) {
        continue;
      }

      const tl = lum[p - width - 1];
      const tc = lum[p - width];
      const tr = lum[p - width + 1];
      const ml = lum[p - 1];
      const mr = lum[p + 1];
      const bl = lum[p + width - 1];
      const bc = lum[p + width];
      const br = lum[p + width + 1];

      const gx = tl + 2 * ml + bl - (tr + 2 * mr + br);
      const gy = tl + 2 * tc + tr - (bl + 2 * bc + br);
      const magnitude = Math.sqrt(gx * gx + gy * gy);

      if (magnitude >= STRONG_EDGE_MIN) {
        stats.strongEdgePixels += 1;
      } else if (magnitude >= SMOOTH_GRADIENT_MIN && magnitude <= SMOOTH_GRADIENT_MAX) {
        stats.smoothGradientPixels += 1;
      }
    }
  }

  return stats;
}

export default analyzeImageDataStats;
